// frontend/src/lib/plansClient.js
// Emergent.sh (Vite + React) için düz JavaScript sürümü

/** 1) Hata -> string çevirici (React'ta "object as child" hatasını bitirir) */
export function errorToMessage(err) {
  try {
    const r = err && err.response;
    const d = (r && r.data) || err.data || err;

    if (d && typeof d === "object") {
      // FastAPI: {detail: "..."} veya {detail: [{msg:"...", loc:[...]}]}
      if (typeof d.detail === "string") return d.detail;
      if (Array.isArray(d.detail) && d.detail[0] && d.detail[0].msg) return String(d.detail[0].msg);
      if (d.msg) return String(d.msg); // pydantic tarzı
      return JSON.stringify(d);
    }
    return String(d != null ? d : err);
  } catch {
    return "Beklenmeyen hata";
  }
}

/** 2) Public/Admin endpoint'leri */
const PUBLIC_URL = "/api/public/plans";     // Register sayfası buradan GET yapar
const ADMIN_ALL_URL = "/api/admin/plans/all"; // Ultra Admin: pasif dahil tüm planlar
const ADMIN_SAVE = "/api/admin/plans/save"; // Ultra Admin kaydı buraya POST eder

/** 3) Basit fetch helper (no-store ile cache busting) */
async function http(input, init) {
  // Token'ı localStorage veya sessionStorage'dan al
  const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
  const ultraToken = localStorage.getItem("ultra_admin_token");
  
  const res = await fetch(String(input), {
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      // Ultra Admin token'ı öncelikli, sonra normal token
      ...(ultraToken ? { Authorization: `Bearer ${ultraToken}` } : {}),
      ...(token && !ultraToken ? { Authorization: `Bearer ${token}` } : {}),
      ...(init && init.headers),
    },
    credentials: "include", // cookie ile çalışan auth için
    ...(init || {}),
  });
  
  if (!res.ok) {
    let data = null;
    try { data = await res.json(); } catch {}
    throw { response: { status: res.status, data } };
  }
  return res.json();
}

/** 4) BroadcastChannel ile gerçek zamanlı senkronizasyon */
const CH_NAME = "plans-updated";
let bc = null;
if (typeof window !== "undefined" && "BroadcastChannel" in window) {
  bc = new BroadcastChannel(CH_NAME);
}

/** 5) usePlans hook'u (React JS) */
import React from "react";

export function usePlans() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const resp = await http(`${PUBLIC_URL}?t=${Date.now()}`); // cache bust
      setData(resp);
      setError(null);
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    if (bc) {
      const onMsg = (e) => { if (e.data === "updated") load(); };
      bc.addEventListener("message", onMsg);
      return () => bc && bc.removeEventListener("message", onMsg);
    }
  }, [load]);

  return { data, plans: (data && data.plans) || [], loading, error, reload: load };
}

/** 5b) Ultra Admin için Pasif planları da içeren hook */
export function useAdminPlans() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const resp = await http(`${ADMIN_ALL_URL}?t=${Date.now()}`);
      setData(resp);
      setError(null);
    } catch (e) {
      setError(errorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    if (bc) {
      const onMsg = (e) => { if (e.data === "updated") load(); };
      bc.addEventListener("message", onMsg);
      return () => bc && bc.removeEventListener("message", onMsg);
    }
  }, [load]);

  return { data, plans: (data && data.plans) || [], loading, error, reload: load };
}

/** 5c) Pasif toggle */
export async function togglePlanActive(packageId) {
  try {
    const resp = await http(`/api/ultra-admin/packages/${packageId}/toggle-active`, {
      method: "PATCH",
    });
    if (bc) bc.postMessage("updated");
    return { ok: true, data: resp };
  } catch (e) {
    return { ok: false, error: errorToMessage(e) };
  }
}

/** 6) Ultra Admin'de kaydetme helper'ı */
export async function savePlans(payload /* { plans: Plan[] } */) {
  try {
    const resp = await http(ADMIN_SAVE, { method: "POST", body: JSON.stringify(payload) });
    try { localStorage.setItem("plans:version", String(resp.version || "")); } catch {}
    if (bc) bc.postMessage("updated"); // tüm sekmelere "yenile" sinyali
    return { ok: true, data: resp };
  } catch (e) {
    return { ok: false, error: errorToMessage(e) };
  }
}

/** 7) Basit liste bileşeni (Register sayfasında kullanışlı) */
export function PlansList({ plans, onSelect }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {plans.filter(p => p.isActive).map((p) => (
        <button
          key={p.id}
          onClick={() => onSelect && onSelect(p)}
          className="rounded-2xl p-4 shadow hover:shadow-md text-left border"
        >
          <div className="text-lg font-semibold flex items-center gap-2">
            {p.title}
            {p.isFeatured ? (
              <span className="text-xs rounded px-2 py-0.5 bg-gray-200">Öne Çıkan</span>
            ) : null}
          </div>
          <div className="mt-2 text-2xl font-bold">
            {(p.currency === "TRY" ? "₺" : p.currency === "USD" ? "$" : "") + p.price}
            <span className="text-sm font-normal">/{p.period === "year" ? "yıl" : "ay"}</span>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            Kullanıcı limiti: {p.userLimit} • Depolama: {p.storageGb} GB • API: {p.apiRatePerDay}/gün
          </div>
          {p.trialDays ? <div className="mt-1 text-sm text-emerald-700">{p.trialDays} gün deneme</div> : null}
          <div className="mt-3 flex flex-wrap gap-1">
            {(p.features || []).slice(0, 6).map((f) => (
              <span key={f} className="text-xs bg-gray-100 rounded px-2 py-0.5">{f}</span>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
}
